// Property-specific form styles (extends shared form styles via theme-aware hook)
import { StyleSheet } from 'react-native';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useSharedFormStyles } from '@/components/common/admin/desktop/sharedFormStyles';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

/**
 * Theme-aware property form stylesheet hook.
 *
 * Wraps the shared form styles with property-specific tokens (button + maintenance
 * schedule overrides). All colors pull from `admin.formInput.*`, `admin.dataTable.*`,
 * and the brand primary blue so dark/light toggles propagate automatically.
 */
export const usePropertyFormStyles = () => {
  const shared = useSharedFormStyles();
  const overrides = usePropertyOverrideStyles();
  return { ...shared, ...overrides };
};

const usePropertyOverrideStyles = () =>
  useThemedStyles(({ components }) => {
    const admin = components.admin;
    return StyleSheet.create({
      // Property-specific button container variant
      buttonContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        padding: 24,
        paddingBottom: 32,
        gap: 12,
      },

      // Override button with brand primary background
      button: {
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 44,
        minWidth: 120,
        backgroundColor: PRIMITIVE_COLORS.resioBlue,
      },

      buttonDisabled: {
        opacity: 0.5,
      },
      buttonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
      },

      // Maintenance Schedule Styles
      maintenanceDay: {
        marginBottom: 20,
      },
      checkboxRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 12,
      },
      checkbox: {
        width: 20,
        height: 20,
        borderWidth: 2,
        borderColor: admin.formInput.border,
        borderRadius: 4,
        marginRight: 12,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: admin.formInput.bg,
      },
      checkboxChecked: {
        borderColor: PRIMITIVE_COLORS.resioBlue,
        backgroundColor: PRIMITIVE_COLORS.resioBlue,
      },
      checkboxInner: {
        width: 10,
        height: 10,
        backgroundColor: '#FFFFFF',
        borderRadius: 2,
      },
      dayLabel: {
        fontSize: 14,
        fontWeight: '500',
        color: admin.dataTable.text,
      },
      timePickersRow: {
        flexDirection: 'row',
        gap: 12,
        marginLeft: 32,
      },
      timePicker: {
        flex: 1,
      },
      timeLabel: {
        fontSize: 12,
        fontWeight: '500',
        color: admin.formInput.label,
        marginBottom: 6,
      },
      timeInput: {
        borderWidth: 1,
        borderColor: admin.formInput.border,
        borderRadius: 8,
        padding: 10,
        fontSize: 14,
        color: admin.formInput.text,
        backgroundColor: admin.formInput.bg,
      },
    });
  });
